import { UIElement } from '../ui-element';
import { UIMessage } from './ui-message';
import { createElement, prerenderElementsTree } from '../../global/render-api';
import { debounce } from '../../global/helpers';
import { Component } from '../../decorators/component.decorator';
import { ComponentState } from '../ui-element.const';
import { Attribute } from '../../decorators/attribute.decorator';
import { Children } from '../../decorators/child.decorator';
import {
    type UIMessageTypeValue,
    MessageConfig,
    type UIMessageLayoutValue,
} from './ui-message.types';
import type { UIModal } from '../modal/ui-modal';
import { NullOr } from '../../global/utils.type';
import styles from './ui-messagebox.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIMessageBox
 * @element ui-messagebox
 * @property {NodeListOf<UIMessage>} messages
 * @property {boolean} [bubbles] {@attr bubbles} bubbles
 * @classdesc Represents a class for <code>ui-messagebox</code> element.
 * Container for ui-message. Provides API and container to work with
 * ui-message elements.
 * @slot {@type "ui-message"}
 * @example
 * <ui-messagebox>
 *   <ui-message type="error">This is an error</ui-message>
 *   <ui-message type="info">This is an info</ui-message>
 * </ui-messagebox>
 */
@Component({
    tag: 'ui-messagebox',
    styles,
})
export class UIMessageBox extends UIElement {
    @Attribute(Boolean) bubbles?: boolean;
    @Children('ui-message') messages: Array<UIMessage> = [];

    private redrawPopoverDebounced!: () => void;
    private closePopoverDebounced!: () => void;
    private intersectionObserver: NullOr<IntersectionObserver> = null;
    private observer: NullOr<IntersectionObserver> = null;
    private popoverElement: NullOr<HTMLElement> = null;
    private _detachedPopover: NullOr<HTMLElement> = null;
    private isIntersected: boolean = false;

    static SCROLL_DEBOUNCE_TIME = 5;
    static RESIZE_DEBOUNCE_TIME = 5;
    static REDRAW_DEBOUNCE_TIME = 50;
    static BUBBLE_LIFETIME = 5000;

    /**
     * @type {string[]}
     */
    static get observedAttributes(): Array<string> {
        return ['bubbles'];
    }

    /**
     * Finds message by given type, code and layout params.
     * @param {UIMessageTypeValue} type
     * @param {string} [code]
     * @param {string} [layout]
     * @returns {UIMessage}
     */
    getMessage(
        type: UIMessageTypeValue,
        code?: string,
        layout?: UIMessageLayoutValue
    ): UIMessage | null {
        let selector = `ui-message[type="${type}"]`;
        if (code) {
            selector += `[code="${code}"]`;
        }
        if (layout) {
            selector += `[layout="${layout}"]`;
        }
        return this.querySelector<UIMessage>(selector);
    }

    /**
     * Finds message by type.
     * @param {string} layout
     * @returns {ArrayLike<UIMessage>}
     */
    getMessagesByLayout(layout: UIMessageLayoutValue): ArrayLike<UIMessage> {
        return this.querySelectorAll<UIMessage>(
            'ui-message[layout="' + layout + '"]'
        );
    }

    /**
     * Finds message by type.
     * @param {UIMessageTypeValue} type
     * @returns {ArrayLike<UIMessage>}
     */
    getMessagesByType(type: UIMessageTypeValue): ArrayLike<UIMessage> {
        return this.querySelectorAll<UIMessage>(
            'ui-message[type="' + type + '"]'
        );
    }

    /**
     * Creates <ui-message/> element with given type, layout, code and closable functionality.
     * @param {UIMessageTypeValue} type
     * @param {string} [code]
     * @param {string} [layout]
     * @param {boolean} [closable]
     * @param {boolean} [persistent]
     * @returns {UIMessage}
     */
    createMessage(
        type: UIMessageTypeValue,
        code?: string,
        layout?: UIMessageLayoutValue,
        closable?: boolean,
        persistent?: boolean
    ): UIMessage {
        const item = createElement<UIMessage>({
            tagName: 'ui-message',
            attributes: {
                type,
                code,
                layout,
                closable,
                persistent,
            },
        })!;
        this.appendChild(item);
        prerenderElementsTree(this, true);
        if (this.bubbles) {
            this.redrawPopoverDebounced();
        }
        return item;
    }

    /**
     * Creates message by given params.
     * @param {UIMessageTypeValue} type
     * @param {string} content
     * @param {string} [code]
     * @param {string} [layout]
     * @param {boolean} [closable]
     * @param {boolean} [persistent]
     * @returns {UIMessageBox}
     */
    addMessage(
        type: UIMessageTypeValue,
        content: string,
        code?: string,
        layout?: UIMessageLayoutValue,
        closable?: boolean,
        persistent?: boolean
    ): UIMessageBox {
        const message = this.findOrCreateMessageBlock(
            type,
            code,
            layout,
            closable,
            persistent
        );
        message.showMessage(content);
        if (this.bubbles) {
            this.redrawPopoverDebounced();
        }
        return this;
    }

    /**
     * Creates messages from given array, message with same type and code will be merged.
     * @param {Array<MessageConfig>} messages
     * @param {boolean} [closable]
     * @param {boolean} [persistent]
     * @returns {UIMessageBox}
     */
    addMessages(
        messages: Array<MessageConfig>,
        closable?: boolean,
        persistent?: boolean
    ): UIMessageBox {
        const mergedMessages = messages.reduce<Record<string, MessageConfig>>(
            (data, message) => {
                const key = [
                    message.layout,
                    message.type,
                    message.code ?? '',
                ].join('-');
                if (!data[key]) {
                    data[key] = {
                        layout: message.layout ?? UIMessage.Layout.Default,
                        type: message.type,
                        messages: message.content ? [message.content] : [],
                        closable: message.closable,
                        code: message.code,
                        persistent: message.persistent,
                    };
                } else if (data[key].messages && message.content) {
                    data[key].messages.push(message.content);
                }
                return data;
            },
            {}
        );

        Object.keys(mergedMessages).forEach((key) => {
            const item = mergedMessages[key];
            const message = this.findOrCreateMessageBlock(
                item.type,
                item.code,
                item.layout,
                closable ?? item.closable,
                persistent ?? item.persistent
            );
            if (Array.isArray(item.messages)) {
                message.showMessage(item.messages.join('\n'));
            }
        });
        return this;
    }

    /**
     * Shortcut for addMessage with predefined "success" type.
     * @param {string} content
     * @param {string} [code]
     * @returns {UIMessageBox}
     */
    addSuccess(content: string, code: string = ''): UIMessageBox {
        return this.addMessage('success', content, code);
    }

    /**
     * Shortcut for addMessage with predefined "error" type.
     * @param {string} content
     * @param {string} [code]
     * @param {string} [layout]
     * @returns {UIMessageBox}
     */
    addError(
        content: string,
        code?: string,
        layout?: UIMessageLayoutValue
    ): UIMessageBox {
        return this.addMessage('error', content, code, layout);
    }

    /**
     * Shortcut for addMessage with predefined "info" type.
     * @param {string} content
     * @param {string} [code]
     * @returns {UIMessageBox}
     */
    addInfoMessage(content: string, code: string = ''): UIMessageBox {
        return this.addMessage('info', content, code);
    }

    /**
     * Shortcut for addMessage with predefined "warning" type.
     * @param {string} content
     * @param {string} [code]
     * @param {string} [layout]
     * @returns {UIMessageBox}
     */
    addWarning(
        content: string,
        code?: string,
        layout?: UIMessageLayoutValue
    ): UIMessageBox {
        return this.addMessage('warning', content, code, layout);
    }

    /**
     * Finds message block by given type and code.
     * Creates one if not found
     * @param {UIMessageTypeValue} type
     * @param {string} [code]
     * @param {string} [layout]
     * @param {boolean} [closable]
     * @param {boolean} [persistent]
     * @returns {UIMessage}
     */
    findOrCreateMessageBlock(
        type: UIMessageTypeValue,
        code?: string,
        layout?: UIMessageLayoutValue,
        closable?: boolean,
        persistent?: boolean
    ): UIMessage {
        return (
            this.getMessage(type, code, layout) ??
            this.createMessage(type, code, layout, closable, persistent)
        );
    }

    /**
     * Removes all messages by given layout.
     * @param {string} layout
     * @returns {UIMessageBox}
     */
    removeMessagesByLayout(layout: UIMessageLayoutValue): UIMessageBox {
        [].forEach.call(
            this.getMessagesByLayout(layout),
            (message: UIMessage) => message.clear()
        );
        if (this.bubbles) {
            this.redrawPopoverDebounced();
        }
        return this;
    }

    /**
     * Removes all messages by given type.
     * @param {UIMessageTypeValue} type
     * @returns {UIMessageBox}
     */
    removeMessagesByType(type: UIMessageTypeValue): UIMessageBox {
        [].forEach.call(this.getMessagesByType(type), (message: UIMessage) =>
            message.clear()
        );
        if (this.bubbles) {
            this.redrawPopoverDebounced();
        }
        return this;
    }

    /**
     * Shortcut for removeMessagesByType with predefined type - "info".
     * @returns {UIMessageBox}
     */
    removeInfoMessages(): UIMessageBox {
        return this.removeMessagesByType('info');
    }

    /**
     * Adds shortcut for removeMessagesByType with predefined type - "success"
     * @returns {UIMessageBox}
     */
    removeSuccessMessages(): UIMessageBox {
        return this.removeMessagesByType('success');
    }

    /**
     * Shortcut for removeMessagesByType with predefined type - "error".
     * @returns {UIMessageBox}
     */
    removeErrors(): UIMessageBox {
        return this.removeMessagesByType('error');
    }

    /**
     * Shortcut for removeMessagesByType with predefined type - "warning".
     * @returns {UIMessageBox}
     */
    removeWarnings(): UIMessageBox {
        return this.removeMessagesByType('warning');
    }

    /**
     * Removes the message by given type, layout and code.
     * @param {UIMessageTypeValue} type
     * @param {string} [code]
     * @param {string} [layout]
     * @returns {UIMessageBox}
     */
    removeMessage(
        type: UIMessageTypeValue,
        code?: string,
        layout?: UIMessageLayoutValue
    ): UIMessageBox {
        const message = this.getMessage(type, code, layout);
        if (!message) {
            return this;
        }
        message.clear();
        if (this.bubbles) {
            this.redrawPopoverDebounced();
        }
        return this;
    }

    /**
     * Adds shortcut for removeMessage with predefined type - "success"
     * @param {string} code
     * @returns {UIMessageBox}
     */
    removeSuccess(code: string): UIMessageBox {
        return this.removeMessage('success', code);
    }

    /**
     * Shortcut for removeMessage with predefined type - "error".
     * @param {string} code
     * @returns {UIMessageBox}
     */
    removeError(code: string): UIMessageBox {
        return this.removeMessage('error', code);
    }

    /**
     * Shortcut for removeMessage with predefined type - "info".
     * @param {string} code
     * @returns {UIMessageBox}
     */
    removeInfoMessage(code: string): UIMessageBox {
        return this.removeMessage('info', code);
    }

    /**
     * Shortcut for removeMessage with predefined type - "warning".
     * @param {string} code
     * @returns {UIMessageBox}
     */
    removeWarning(code: string): UIMessageBox {
        return this.removeMessage('warning', code);
    }

    /**
     * Adds shortcut for remove all messages.
     * @returns {UIMessageBox}
     */
    removeAllMessages() {
        this.removeSuccessMessages();
        this.removeInfoMessages();
        this.removeWarnings();
        this.removeErrors();
        return this;
    }

    /**
     * Closes detached popover.
     */
    closeDetachedPopover() {
        if (!this._detachedPopover) {
            return;
        }
        if (!this.popoverElement) {
            return;
        }
        const modal = this.popoverElement.closest<UIModal>('ui-modal');
        modal?.close();

        this.popoverElement = null;
        this._detachedPopover = null;
    }

    /**
     * Opens detached popover.
     * @fires event:modal-open
     */
    openDetachedPopover() {
        if (this._detachedPopover) {
            return;
        }
        this._detachedPopover = this.popoverElement;
        this.dispatchCustomEvent('modal-open', {
            type: 'bubble',
            content: this.popoverElement,
            params: {
                classList: {
                    '-messagebox': true,
                },
                attributes: {
                    closable: true,
                },
            },
        });
    }

    displayInPopover() {
        const nodeList = [].map.call(this.messages, (entry: UIMessage) =>
            entry.cloneNode(true)
        );
        if (nodeList.length) {
            if (!this.popoverElement) {
                this.popoverElement = this.createElement({
                    tagName: 'div',
                    classList: {
                        'ui-messagebox__popover': true,
                    },
                });
                this.openDetachedPopover();
                this.subscribeIntersection();
            }
            this.closePopoverDebounced();
            this.rebuildChildren.call(
                this.popoverElement,
                nodeList as Array<UIMessage>
            );
        } else {
            if (this.popoverElement) {
                this.closeDetachedPopover();
                this.unsubscribeIntersection();
            }
        }
    }

    unsubscribeIntersection() {
        this.observer?.disconnect();
    }

    subscribeIntersection() {
        this.observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && this.popoverElement) {
                        this.closeDetachedPopover();
                        this.unsubscribeIntersection();
                    }
                });
            },
            { root: null, rootMargin: '0px' }
        );
        this.observer.observe(this);
    }

    redrawPopover() {
        if (!this.isIntersected && this.bubbles) {
            this.displayInPopover();
        }
    }

    checkIntersection() {
        if (this.isIntersected && this.popoverElement) {
            this.closeDetachedPopover();
            this.unsubscribeIntersection();
        }
    }

    observeIntersection() {
        if (this.bubbles && !this.intersectionObserver) {
            this.intersectionObserver = new IntersectionObserver(
                (entries) => {
                    const entry = entries[0];
                    this.isIntersected = entry.isIntersecting;
                },
                { root: null }
            );
            this.intersectionObserver.observe(this);
        } else if (!this.bubbles && this.intersectionObserver) {
            this.intersectionObserver.disconnect();
            this.intersectionObserver = null;
        }
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: string,
        oldValue: string | null,
        newValue: string | null
    ) {
        if (this.state !== ComponentState.Hydrated) {
            return;
        }

        switch (name) {
            case 'bubbles':
                this.observeIntersection();
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * @inheritDoc
     */
    render() {
        this.setAttribute('role', 'group');
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        this.redrawPopoverDebounced = debounce(
            this.redrawPopover.bind(this),
            UIMessageBox.REDRAW_DEBOUNCE_TIME
        );
        this.closePopoverDebounced = debounce(
            this.closeDetachedPopover.bind(this),
            UIMessageBox.BUBBLE_LIFETIME
        );
        this.observeIntersection();
    }

    /**
     * @inheritDoc
     */
    disconnect() {
        this.closeDetachedPopover();
    }
}
